import { useEffect, useRef, useState } from 'react';
import { Check, Pause, Play, RotateCcw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatClock } from '@/lib/ground/utils';

interface SectionTimerProps {
  seconds: number;
  done: boolean;
  onDone: () => void;
  className?: string;
}

/**
 * A small guided countdown used by Foundations micro-sections and protocol
 * sessions. The parent owns the "done" state; this control just runs the clock
 * and reports when it reaches zero.
 */
export function SectionTimer({ seconds, done, onDone, className }: SectionTimerProps) {
  const [remaining, setRemaining] = useState(seconds);
  const [running, setRunning] = useState(false);
  const remainingRef = useRef(seconds);
  const onDoneRef = useRef(onDone);

  useEffect(() => {
    onDoneRef.current = onDone;
  }, [onDone]);

  useEffect(() => {
    if (!running) return;
    const id = window.setInterval(() => {
      remainingRef.current = Math.max(0, remainingRef.current - 1);
      setRemaining(remainingRef.current);
      if (remainingRef.current === 0) {
        setRunning(false);
        onDoneRef.current();
      }
    }, 1000);
    return () => window.clearInterval(id);
  }, [running]);

  const reset = () => {
    remainingRef.current = seconds;
    setRemaining(seconds);
    setRunning(false);
  };

  const progress = seconds > 0 ? ((seconds - remaining) / seconds) * 100 : 0;

  return (
    <div className={cn('flex flex-wrap items-center gap-3', className)}>
      <div
        className={cn(
          'flex min-w-[4.5rem] items-center justify-center rounded-lg border px-3 py-1.5 font-display text-lg tabular-nums',
          done ? 'border-primary/40 text-primary' : 'border-border text-foreground',
        )}
        aria-label={done ? 'Timer complete' : `Time remaining ${formatClock(remaining)}`}
      >
        {done ? (
          <span className="flex items-center gap-1.5 text-base">
            <Check className="size-4" />
            Done
          </span>
        ) : (
          formatClock(remaining)
        )}
      </div>

      {!done && (
        <div className="flex items-center gap-2">
          {running ? (
            <Button size="sm" variant="secondary" className="gap-1.5" onClick={() => setRunning(false)}>
              <Pause className="size-3.5" />
              Pause
            </Button>
          ) : (
            <Button size="sm" className="gap-1.5" onClick={() => setRunning(true)}>
              <Play className="size-3.5" />
              {remaining === seconds ? 'Start' : 'Resume'}
            </Button>
          )}
          {remaining !== seconds && (
            <Button
              size="sm"
              variant="ghost"
              className="gap-1.5 text-muted-foreground"
              onClick={reset}
            >
              <RotateCcw className="size-3.5" />
              Reset
            </Button>
          )}
        </div>
      )}

      {!done && (
        <div className="h-1 w-full overflow-hidden rounded-full bg-foreground/10 sm:ml-2 sm:w-auto sm:flex-1">
          <div
            className="h-full rounded-full bg-primary transition-all duration-1000 ease-linear"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
}
